import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, InputGroup, Dropdown, Badge, Spinner, Form } from 'react-bootstrap';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaFilter, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import Layout from '../components/layout/Layout';
import ProductCard from '../components/ProductCard';
import SearchBar from '../components/common/SearchBar';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';
import api from '../services/api';
import './Products.css';

// Categories
const CATEGORIES = [
  'All Categories',
  'Textbooks',
  'Electronics',
  'Stationery',
  'Furniture',
  'Clothing',
  'Food'
];

const Products = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, userRole, userData } = useAuth();
  const { addToCart } = useCart();
  const { addToWishlist } = useWishlist();
  
  // Get search query from URL if present
  const queryParams = new URLSearchParams(location.search);
  const querySearch = queryParams.get('search') || '';
  const queryCategory = queryParams.get('category') || 'All Categories';
  
  // State for products and filters
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState(querySearch);
  const [selectedCategory, setSelectedCategory] = useState(queryCategory);
  const [showFilters, setShowFilters] = useState(false);
  const [sortOption, setSortOption] = useState('default');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [locationFilter, setLocationFilter] = useState('mySchool'); // 'mySchool' or 'all'
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Fetch products function that can be called manually
  const fetchProducts = useCallback(async () => {
    try {
      let url = '/products/';
      if (searchTerm) {
        url += `?search=${encodeURIComponent(searchTerm)}`;
      }
      const response = await api.get(url);
      const apiProducts = Array.isArray(response.data.results) ? response.data.results : [];
      // Filter out products with status 'hidden'
      const availableProducts = apiProducts.filter(product => product.status !== 'hidden');
      setProducts(availableProducts);
      return availableProducts;
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
      return [];
    }
  }, [searchTerm]);
  
  // Initial fetch on component mount and when search term changes
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts, location.key]);
  
  // Handle pull to refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchProducts();
    } finally {
      setIsRefreshing(false);
    }
  };
  
  // Apply filters and search
  // Apply filters and sorting
  useEffect(() => {
    let tempProducts = [...products];

    // Filter by location
    if (locationFilter === 'mySchool' && userData && userData.school) {
      tempProducts = tempProducts.filter(p => p.seller?.school === userData.school);
    }
    
    // Filter by category
    if (selectedCategory !== 'All Categories') {
      tempProducts = tempProducts.filter(product => product.category === selectedCategory);
    }
    
    // Filter by price range
    if (priceRange.min || priceRange.max) {
      tempProducts = tempProducts.filter(product => {
        const numericPrice = parseFloat(product.price);
        const min = priceRange.min ? parseFloat(priceRange.min) : 0;
        const max = priceRange.max ? parseFloat(priceRange.max) : Infinity;
        return numericPrice >= min && numericPrice <= max;
      });
    }
    
    // Apply sorting
    switch (sortOption) {
      case 'price-low-high':
        tempProducts.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        break;
      case 'price-high-low':
        tempProducts.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
        break;
      case 'name-a-z':
        tempProducts.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-z-a':
        tempProducts.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'newest':
      default:
        // In a real app, this would sort by date added
        break;
    }
    
    setFilteredProducts(tempProducts);
  }, [products, selectedCategory, priceRange, sortOption, locationFilter, userData]);
  
  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (searchTerm) {
      params.set('search', searchTerm);
    }
    
    if (selectedCategory !== 'All Categories') {
      params.set('category', selectedCategory);
    }
    
    navigate({ search: params.toString() }, { replace: true });
  }, [searchTerm, selectedCategory, navigate]);
  
  // Handle search form submission
  const handleSearch = (searchTerm = '') => {
    const params = new URLSearchParams();
    
    if (searchTerm) params.set('search', searchTerm);
    if (selectedCategory && selectedCategory !== 'All Categories') {
      params.set('category', selectedCategory);
    }
    
    navigate(`/products?${params.toString()}`);
  };
  
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };
  
  const handlePriceRangeChange = (e, type) => {
    setPriceRange(prev => ({
      ...prev,
      [type]: Number(e.target.value)
    }));
  };
  
  const handleSortChange = (sortOption) => {
    setSortOption(sortOption);
  };
  
  const handleAddToCart = (product) => {
    addToCart(product);
    alert(`${product.name} added to cart!`);
  };
  
  const handleAddToWishlist = (product) => {
    addToWishlist(product);
    alert(`${product.name} added to wishlist!`);
  };
  
  return (
    <Layout>
      <Container>
        <h2 className="mb-4">Products</h2>
        
        {/* Search and Filter Bar */}
        <div className="products-filter-bar mb-4">
          <Row className="align-items-center">
            <Col xs={12} md={8} className="mb-3 mb-md-0">
              <SearchBar 
                placeholder="Search products..."
                value={searchTerm}
                onChange={setSearchTerm}
                onSubmit={handleSearch}
                className="w-100"
              />
            </Col>
            <Col md={4} className="d-flex justify-content-end">
              <Button 
                variant="outline-secondary" 
                onClick={() => setShowFilters(!showFilters)}
                className="me-2"
              >
                <FaFilter /> Filters
              </Button>
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" id="dropdown-location">
                  {locationFilter === 'mySchool' ? 'My School' : 'All Schools'}
                </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item 
                      active={locationFilter === 'mySchool'}
                      onClick={() => setLocationFilter('mySchool')}
                    >
                      My School
                    </Dropdown.Item>
                    <Dropdown.Item 
                      active={locationFilter === 'all'}
                      onClick={() => setLocationFilter('all')}
                    >
                      All Schools
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>

                <Dropdown>
                  <Dropdown.Toggle variant="outline-secondary" id="dropdown-sort">
                    <FaSortAmountDown className="me-2" />
                    Sort By: {sortOption.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item 
                      active={sortOption === 'default'}
                      onClick={() => handleSortChange('default')}
                    >
                      Default
                    </Dropdown.Item>
                    <Dropdown.Item 
                      active={sortOption === 'price-low-high'}
                      onClick={() => handleSortChange('price-low-high')}
                    >
                      Price: Low to High
                    </Dropdown.Item>
                    <Dropdown.Item 
                      active={sortOption === 'price-high-low'}
                      onClick={() => handleSortChange('price-high-low')}
                    >
                      Price: High to Low
                    </Dropdown.Item>
                    <Dropdown.Item 
                      active={sortOption === 'name-a-z'}
                      onClick={() => handleSortChange('name-a-z')}
                    >
                      Name: A to Z
                    </Dropdown.Item>
                    <Dropdown.Item 
                      active={sortOption === 'name-z-a'}
                      onClick={() => handleSortChange('name-z-a')}
                    >
                      Name: Z to A
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
            </Row>
            
            {/* Advanced Filters */}
            {showFilters && (
              <div className="mt-3">
                <Row>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Category</Form.Label>
                      <Form.Select 
                        value={selectedCategory}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                      >
                        {CATEGORIES.map((category, index) => (
                          <option key={index} value={category}>
                            {category}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={8}>
                    <Form.Label>Price Range (GH₵{priceRange.min} - GH₵{priceRange.max})</Form.Label>
                    <Row>
                      <Col>
                        <Form.Control
                          type="number"
                          min="0"
                          max="1000"
                          value={priceRange.min}
                          onChange={(e) => handlePriceRangeChange(e, 'min')}
                        />
                      </Col>
                      <Col className="text-center">to</Col>
                      <Col>
                        <Form.Control
                          type="number"
                          min="0"
                          max="1000"
                          value={priceRange.max}
                          onChange={(e) => handlePriceRangeChange(e, 'max')}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>
            )}
        </div>
        
        {/* Results Summary */}
        <div className="mb-4 d-flex justify-content-between align-items-center">
          <p className="mb-0">
            Showing {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
            {searchTerm && <span> for "<strong>{searchTerm}</strong>"</span>}
            {selectedCategory !== 'All Categories' && (
              <Badge bg="primary" className="ms-2">{selectedCategory}</Badge>
            )}
          </p>
          {(searchTerm || selectedCategory !== 'All Categories') && (
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('All Categories');
                navigate('/products');
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>
        
        {/* Products Grid with Pull to Refresh */}
        <div className="position-relative">
          <PullToRefresh
            onRefresh={handleRefresh}
            pullingContent={
              <div className="d-flex justify-content-center py-2">
                <Spinner animation="border" size="sm" className="me-2" />
                <span>Pull down to refresh...</span>
              </div>
            }
            refreshingContent={
              <div className="d-flex justify-content-center py-2">
                <Spinner animation="border" size="sm" className="me-2" />
                <span>Refreshing products...</span>
              </div>
            }
            className="pull-to-refresh-container"
          >
            <div>
              {filteredProducts.length > 0 ? (
                <Row>
                  {filteredProducts.map(product => (
                    <Col key={product.id} md={4} sm={6} className="mb-4">
                      <ProductCard product={product} />
                    </Col>
                  ))}
                </Row>
              ) : (
                <div className="text-center py-5">
                  <h4>No products found</h4>
                  <p className="text-muted">
                    Try adjusting your search or filter criteria
                  </p>
                </div>
              )}
            </div>
          </PullToRefresh>
        </div>

      </Container>
    </Layout>
  );
};

export default Products;
